import React, { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, AppBar, Toolbar, Typography, Box, Avatar, ListItemButton, Alert, AlertTitle, Button, Badge, Popover, Divider, LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import { Build, OpenInNew, Menu as MenuIcon, Logout, Settings, CalendarMonth, QueryStats, ManageAccounts, Notifications, Language, RocketLaunch } from '@mui/icons-material';
import { AdminDashboardViewModel } from './AdminDashboardViewModel';
import LoadingSpinner from '../../../components/global/LoadingSpinner';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { formatDate, formatTime } from '../../../time/timeUtils';
import { resolveAdminBookingUxConfig } from '../../../utils/bookingUxConfig';
import { useTenant } from '../../../tenancy/TenantContext';
import { useTenantPublishStatus } from '../../../sitePublishing/hooks';
import { auth, trackEvent } from '../../../firebase/firebase';

const AdminDashboard: React.FC = () => {
  const { tenantId } = useTenant();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const {
    bookingNotifications,
    unreadBookingNotificationCount,
    markBookingNotificationsRead,
  } = useAdminData();
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<HTMLElement | null>(null);

  const {
    drawerOpen,
    lockDrawer,
    alertMessage,
    showPaymentWarningBanner,
    paymentLockDaysRemaining,
    isSubscriptionOwner,
    location,
    showAlertBanner,
    dismissAlert,
    profilePicUrl,
    employeeQuery,
    employeesQuery,
    superAdminQuery,
    scheduleQuery,
    businessConfigQuery,
    onboardingStatusQuery,
    isSelfServiceOnboarding,
    checkForMissingWorkHoursMutation,
    maintainAllActiveEmployeeSchedulesMutation,
    toggleDrawer,
    navigateTo,
    sendTo404Page,
    handleSignOut,
    isLoading,
    isUpdatingSchedule,
    isSetUpError
  } = AdminDashboardViewModel();
  const adminMaintenanceRunKeyRef = useRef<string | null>(null);
  const missingHoursRunKeyRef = useRef<string | null>(null);
  const scheduleSignature = React.useMemo(() => {
    if (!scheduleQuery.data) {
      return '';
    }
    return Array.from(scheduleQuery.data.entries())
      .map(([date, bitString]) => `${date}:${bitString}`)
      .sort()
      .join('|');
  }, [scheduleQuery.data]);

  // This will be to check for any warnings. 
  // In this case its just to show if there is a missing
  // day schedule in the next (BUFFER_PERIOD * 2) days
  useEffect(() => {
    if (isSubscriptionOwner && (onboardingStatusQuery.isLoading || onboardingStatusQuery.isFetching)) return;
    // Onboarding creates availability explicitly. Do not let normal dashboard
    // maintenance pre-populate schedules before the owner reaches that step.
    if (isSelfServiceOnboarding) return;

    const employee = employeeQuery.data;
    if (!employee) {
      return;
    }

    let triggeredMaintenanceRun = false;

    if (employee.admin) {
      if (
        employeesQuery.data &&
        businessConfigQuery.data &&
        !maintainAllActiveEmployeeSchedulesMutation.isPending
      ) {
        const now = Date.now();
        const nextDueAt = businessConfigQuery.data.preferences?.nextEmployeeScheduleMaintenanceAt ?? 0;
        const isDue = nextDueAt <= now;
        if (isDue) {
          const runKey = `${employee.id}:${nextDueAt}:${employeesQuery.data.length}`;
          if (adminMaintenanceRunKeyRef.current !== runKey) {
            adminMaintenanceRunKeyRef.current = runKey;
            maintainAllActiveEmployeeSchedulesMutation.mutate();
            triggeredMaintenanceRun = true;
          }
        }
      }
    }

    if (triggeredMaintenanceRun) {
      return;
    }

    if (scheduleQuery.data && !checkForMissingWorkHoursMutation.isPending) {
      const runKey = `${employee.id}:${scheduleSignature}`;
      if (missingHoursRunKeyRef.current === runKey) {
        return;
      }
      missingHoursRunKeyRef.current = runKey;
      checkForMissingWorkHoursMutation.mutate();
    }
  }, [
    employeeQuery.data,
    employeesQuery.data,
    businessConfigQuery.data,
    scheduleQuery.data,
    scheduleSignature,
    checkForMissingWorkHoursMutation,
    isSelfServiceOnboarding,
    isSubscriptionOwner,
    maintainAllActiveEmployeeSchedulesMutation,
    onboardingStatusQuery.isFetching,
    onboardingStatusQuery.isLoading,
  ]);

  // navigate to 404 if theres an error getting the employee data
  if (isSetUpError) {
    trackEvent('setup_error', { "screen": "AdminDashboard" });
    // handleSignOut();
    sendTo404Page();

  }

  // Extract employee initials or default to 'A' if data is unavailable
  const employeeInitials = employeeQuery.data
    ? `${employeeQuery.data.firstName[0]}${employeeQuery.data.lastName[0]}`.toUpperCase()
    : 'A';

  // Define base menu items
  const menuItems = isSelfServiceOnboarding
    ? [
      { name: 'Launch Checklist', path: 'onboarding', icon: <RocketLaunch /> },
    ]
    : [
      { name: 'Schedule', path: 'schedule-hub', icon: <CalendarMonth /> },
      { name: 'Analytics', path: 'analytics', icon: <QueryStats /> },
      { name: 'Settings', path: 'settings', icon: <Settings /> },
    ];

  const canManageEmployees = employeeQuery.data?.admin === true;
  const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;

  if (canManageEmployees && !isSelfServiceOnboarding) {
    menuItems.splice(2, 0, { name: `Manage ${resourcePlural}`, path: 'manage-employees', icon: <ManageAccounts /> });
  }

  // Publishing authorization is enforced by the callable functions. The dashboard
  // does not need to call publish status merely to decide whether to render this link.
  if (!isSelfServiceOnboarding && (canManageEmployees || superAdminQuery.data === true)) {
    menuItems.splice(menuItems.length - 1, 0, { name: 'Site Builder', path: 'site', icon: <Language /> });
  }

  // We should only show the Payment Portal if payments are enabled
  // and either the user is the owner or the business is not using its own Stripe account

  if (superAdminQuery.data === true && !isSelfServiceOnboarding) {
    menuItems.push({
      name: 'SuperAdmin Tools',
      path: 'superadmin',
      icon: <Build />, // you can change the icon
    });
  }

  // Publish status is admin-only, which is also who the link is for.
  const publishStatusQuery = useTenantPublishStatus(!isSelfServiceOnboarding && (canManageEmployees || superAdminQuery.data === true));
  const liveSiteUrl = publishStatusQuery.data?.lifecycleState === 'active' && publishStatusQuery.data.readiness.sitePublished
    ? publishStatusQuery.data.publicUrl
    : undefined;

  const [dashboardTitle, setDashboardTitle] = useState(menuItems[0].name); // default value
  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true;
  const showDesktopBell = isDesktop && isOrchestratorMode;
  const isNotificationsOpen = Boolean(notificationsAnchorEl);
  const recentNotifications = bookingNotifications.slice(0, 20);
  const isFounderTenantView = superAdminQuery.data === true && employeeQuery.data?.id !== auth.currentUser?.uid;
  const dashboardEmployeeName = employeeQuery.data
    ? `${employeeQuery.data.firstName} ${employeeQuery.data.lastName}`.trim()
    : '';

  // Keep header title in sync with current route
  useEffect(() => {
    const path = location.pathname.replace(/^\/*admin\/*/, '');
    const segment = path.split('/')[0];

    const routeTitleMap: Record<string, string> = {
      'schedule-hub': 'Schedule',
      'settings': 'Settings',
      'manage-employees': `Manage ${resourcePlural}`,
      'my-profile': 'My Profile',
      'edit-work-hours': 'My Work Hours',
      'add-employee': `Add ${resourceSingular}`,
      'manage-services': 'Manage Services',
      'gallery': 'Photo Gallery',
      'website-details': 'Business Details',
      'payment-portal': 'Payment Portal',
      'superadmin': 'SuperAdmin Tools',
      'manage-subscription': 'Manage Scheduify Subscription',
      'appointments': 'My Appointments',
      'analytics': 'Analytics',
      'customers': 'Customers',
      'missing-subscription': 'Subscription Required',
      'contact-support': 'Contact Support',
      'site': 'Site Builder',
      'onboarding': 'Launch Checklist',
    };

    let title = routeTitleMap[segment];
    if (!title) {
      // Fallbacks: default to Schedule for dashboard/root, or generic Admin
      title = segment === '' || segment === 'dashboard' ? 'Schedule' : 'Admin';
    }
    setDashboardTitle(title);
  }, [location.pathname, resourcePlural, resourceSingular]);

  // Render loading state
  if (isLoading) {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <LoadingSpinner label="Loading your dashboard" />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => {
              if (!lockDrawer) toggleDrawer(true);
            }}
            disabled={lockDrawer}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {dashboardTitle}
          </Typography>
          {showDesktopBell && (
            <>
              <IconButton
                color="inherit"
                aria-label="notifications"
                onClick={(event) => {
                  setNotificationsAnchorEl(event.currentTarget);
                  markBookingNotificationsRead();
                }}
              >
                <Badge
                  badgeContent={unreadBookingNotificationCount > 0 ? unreadBookingNotificationCount : null}
                  color="error"
                  max={99}
                >
                  <Notifications />
                </Badge>
              </IconButton>
              <Popover
                open={isNotificationsOpen}
                anchorEl={notificationsAnchorEl}
                onClose={() => setNotificationsAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ width: 360, maxWidth: '90vw', maxHeight: 420, overflowY: 'auto' }}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Recent Bookings
                    </Typography>
                  </Box>
                  <Divider />
                  {recentNotifications.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No new booking notifications yet.
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {recentNotifications.map((notification) => (
                        <ListItem
                          key={notification.id}
                          disablePadding
                          onClick={() => {
                            setNotificationsAnchorEl(null);
                            navigateTo(notification.appointment.id);
                          }}
                        >
                          <ListItemButton>
                            <ListItemText
                              primary={`${notification.appointment.customerFirstName} ${notification.appointment.customerLastName}`}
                              secondary={`${notification.appointment.appointmentTitle} • ${formatDate(notification.appointment.appointmentDate)} • ${formatTime(notification.appointment.appointmentTime)}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Popover>
            </>
          )}
          {isSelfServiceOnboarding ? (
            <Avatar src={profilePicUrl || undefined} aria-label="Account avatar">
              {!profilePicUrl ? employeeInitials : undefined}
            </Avatar>
          ) : (
            <Button color="inherit" onClick={() => navigateTo('my-profile')}>
              {profilePicUrl ? <Avatar src={profilePicUrl} /> : <Avatar>{employeeInitials}</Avatar>}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {!lockDrawer && (
        <Drawer anchor="left" open={drawerOpen} onClose={() => toggleDrawer(false)}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding
                onClick={() => {
                  navigateTo(item.path);
                }}>
                <ListItemButton selected={location.pathname === `/admin/${item.path}`}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              </ListItem>
            ))}
            {liveSiteUrl ? (
              <ListItem disablePadding>
                <ListItemButton component="a" href={liveSiteUrl} target="_blank" rel="noopener noreferrer"
                  onClick={() => toggleDrawer(false)}>
                  <ListItemIcon><OpenInNew /></ListItemIcon>
                  <ListItemText primary="Open my site" />
                </ListItemButton>
              </ListItem>
            ) : null}
            <ListItem disablePadding onClick={handleSignOut}>
              <ListItemButton>
                <ListItemIcon><Logout /></ListItemIcon>
                <ListItemText primary="Sign out" />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>
      )}


      <Box sx={{ mt: 8, p: isSelfServiceOnboarding && location.pathname === '/admin/onboarding' ? 0 : 2 }}>
        {isFounderTenantView && (
          <Alert
            sx={{ mb: 2 }}
            severity="info"
            action={(
              <Button color="inherit" size="small" onClick={() => navigateTo('superadmin')}>
                SuperAdmin Tools
              </Button>
            )}
          >
            <AlertTitle>Founder tenant view</AlertTitle>
            {`Viewing ${tenantId}${dashboardEmployeeName ? ` with ${dashboardEmployeeName} as the dashboard context` : ''}. Your SuperAdmin account remains signed in.`}
          </Alert>
        )}
        {/* ---vvvvvv---WARNING BANNER ---vvvvvv--- */}
        {showPaymentWarningBanner && (
          <Alert
            sx={{ mb: 2 }}
            severity="warning"
            action={isSubscriptionOwner ? (
              <Button color="inherit" size="small" onClick={() => navigateTo('manage-subscription')}>
                Manage Subscription
              </Button>
            ) : undefined}
          >
            <AlertTitle>Payment Required</AlertTitle>
            {`Your website will be locked in ${paymentLockDaysRemaining} day${paymentLockDaysRemaining === 1 ? '' : 's'} unless payment is completed.`}
            {!isSubscriptionOwner ? ' Please contact the owner to complete payment and update billing.' : ''}
          </Alert>
        )}
        {isUpdatingSchedule ? (
          <Box role="status" aria-live="polite" sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Updating your schedule…</Typography>
            <LinearProgress sx={{ mt: 0.5, height: 3, borderRadius: 999 }} />
          </Box>
        ) : null}
        {showAlertBanner && (
          <Alert
            sx={{
              mb: 2,
              "& .MuiAlert-action": {
                alignItems: "center",
                display: "flex",
              },
            }}
            severity="info"
            onClose={dismissAlert}
          // action={
          //   <IconButton color="inherit" onClick={() => navigateTo('edit-work-hours')}>
          //     <EditCalendarOutlined />

          //   </IconButton>
          // }
          >
            <AlertTitle>Schedule auto-updated</AlertTitle>
            {alertMessage}
          </Alert>
        )}
        <Outlet />
      </Box>
    </>
  );
};

export default AdminDashboard;
